/**
 * Utility functions for date formatting
 */

/**
 * Formats a date string to "01-Aug-2025" format (DD-Mon-YYYY)
 * @param {string|number|Date} dateString - The date string to format
 * @returns {string} - Formatted date string or original value if invalid
 */
export function formatToDDMonYYYY(dateString) {
  try {
    // Return empty string if no date provided
    if (!dateString || dateString === null || dateString === undefined || dateString === '' || (typeof dateString === 'string' && dateString.trim() === '')) {
      return '';
    }
    
    // Handle different date formats that might be in the data
    let date;
    
    // If it's already a Date object
    if (dateString instanceof Date) {
      date = dateString;
    } 
    // If it's a number, it might be a timestamp or Excel serial date
    else if (typeof dateString === 'number') {
      // If it's a reasonable timestamp (after 1970)
      if (dateString > 1000000) {
        date = new Date(dateString);
      } 
      // If it's likely an Excel serial date (smaller number)
      else if (dateString >= 1 && dateString < 100000) {
        try {
          // Convert Excel date number to JavaScript Date
          // Excel's epoch is December 30, 1899 (day 0)
          const excelEpoch = new Date(1899, 11, 30);
          date = new Date(excelEpoch.getTime() + (dateString * 24 * 60 * 60 * 1000));
          
          // Handle Excel's leap year bug
          if (dateString >= 60 && dateString < 61) {
            date.setDate(date.getDate() - 1);
          }
        } catch (excelError) {
          console.warn('Failed to parse Excel date number:', dateString, excelError);
          return String(dateString);
        }
      } else {
        return String(dateString);
      }
    } 
    // If it's a string
    else if (typeof dateString === 'string') {
      const cleanedDate = dateString.trim();
      
      // Try parsing as ISO date first (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}/.test(cleanedDate)) {
        date = new Date(cleanedDate);
      } 
      // Try parsing DD-Mon-YY or DD-Mon-YYYY format (01-Jan-25 or 01-Jan-2025)
      else if (/^\d{1,2}-[A-Za-z]{3}-\d{2,4}$/.test(cleanedDate)) {
        const parts = cleanedDate.split('-');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                             'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const month = monthNames.indexOf(parts[1]);
          let year = parseInt(parts[2], 10);
          
          // Handle 2-digit years (assume 20XX if <= 50, 19XX if > 50)
          if (year < 100) {
            year = year <= 50 ? 2000 + year : 1900 + year;
          }
          
          if (month !== -1 && !isNaN(day) && !isNaN(year)) {
            date = new Date(year, month, day);
          } else {
            // If parsing fails, try standard date parsing
            date = new Date(cleanedDate);
          }
        } else {
          // If splitting fails, try standard date parsing
          date = new Date(cleanedDate);
        }
      }
      // Try parsing dates with slashes or dashes (DD/MM/YYYY or DD-MM-YYYY)
      else if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/.test(cleanedDate)) {
        const parts = cleanedDate.split(/[\/\-]/);
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
          let year = parseInt(parts[2], 10);
          
          // Handle 2-digit years (assume 20XX if <= 50, 19XX if > 50)
          if (year < 100) {
            year = year <= 50 ? 2000 + year : 1900 + year;
          }
          
          date = new Date(year, month, day);
        }
      }
      // Try parsing as a timestamp
      else if (!isNaN(Date.parse(cleanedDate))) {
        date = new Date(cleanedDate);
      }
      // If all else fails, return original string
      else {
        return dateString;
      }
    } else {
      // For any other type, try to convert to date
      date = new Date(dateString);
    }
    
    // Check if date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return dateString; // Return original if invalid date
    }
    
    // Format to "01-Aug-2025" (DD-Mon-YYYY format with 4-digit year)
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    // Use 4-digit year (as per user requirement)
    return `${day}-${month}-${year}`;
  } catch (error) {
    // If any error occurs, log it and return the original string
    console.warn('Error formatting date:', dateString, error);
    return dateString || '';
  }
}